import { Component, AfterViewInit, EventEmitter, Output, Input } from '@angular/core';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements AfterViewInit {
  @Output() placeSelected = new EventEmitter<any>();
  @Input() set reset(value: boolean) {
    if (value) {
      this.resetMap();
    }
  }
  @Input() set initialLocation(value: any) {
    if (value && this.map && this.marker) {
      this.populateLocation(value);
    } else if (value) {
      // Store for later when map is ready
      this._pendingLocation = value;
    }
  }

  apiLoaded: Promise<boolean>;
  map: google.maps.Map | undefined;
  marker: google.maps.Marker | undefined;
  autocomplete: google.maps.places.Autocomplete | undefined;
  private _pendingLocation: any = null;

  constructor() {
    // Ensure the Maps API is loaded
    this.apiLoaded = new Promise((resolve, reject) => {
      if ((window as any).google && (window as any).google.maps) {
        resolve(true);
      } else {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places`;
        script.onload = () => resolve(true);
        script.onerror = () => reject(false);
        document.head.appendChild(script);
      }
    });
  }

  ngAfterViewInit(): void {
    this.apiLoaded.then(() => {
      const input = document.getElementById('autocomplete') as HTMLInputElement;

      if (input) {
        this.autocomplete = new google.maps.places.Autocomplete(input);
        const mapContainer = document.getElementById('map') as HTMLElement;

        // Initialize the map
        this.map = new google.maps.Map(mapContainer, {
          center: { lat: 37.7749, lng: -122.4194 }, // Default center (San Francisco, for example)
          zoom: 13,
        });

        // Initialize a marker
        this.marker = new google.maps.Marker({
          map: this.map,
        });

        // Populate location if pending
        if (this._pendingLocation) {
          this.populateLocation(this._pendingLocation);
          this._pendingLocation = null;
        }

        // Listen for place selection
        this.autocomplete.addListener('place_changed', () => {
          const place = this.autocomplete!.getPlace();
          if (place.geometry && place.geometry.location) {
            const location = place.geometry.location;

            // Get formatted details
            const locationDetails = [{
              place: place.name || '',
              city: this.getAddressComponent(place, 'locality'),
              country: this.getAddressComponent(place, 'country'),
              longitude: location.lng(),
              latitude: location.lat(),
              countryISO: this.getAddressComponent(place, 'country', 'short_name'),
              address: place.formatted_address,
            }];
            // Center the map and update the marker
            this.map?.setCenter(location); 
            this.marker?.setPosition(location); 
            this.marker?.setVisible(true);

            // Emit the formatted location object
            this.placeSelected.emit(locationDetails);
          } else {
            console.warn('No geometry for selected place');
          }
        });
      }
    });
  }

  private countryAlpha2ToAlpha3: { [key: string]: string } = {
    MK: 'MKD',
    AE: 'ARE',
    RS: 'SRB',
    // Add more countries as needed
  };
  

  /**
   * Helper method to retrieve specific address components
   * @param place - Google PlaceResult object
   * @param type - Address component type to search for (e.g., 'locality', 'country')
   * @param nameType - Specific name type ('long_name' or 'short_name')
   * @returns - The corresponding address component or null
   */
  private getAddressComponent(
    place: google.maps.places.PlaceResult,
    type: string,
    nameType: 'long_name' | 'short_name' = 'long_name'
  ): string | null {
    const component = place.address_components?.find((comp) => comp.types.includes(type));
    if (!component) return null;
  
    const value = component[nameType];
    
    if (type === 'country' && nameType === 'short_name') {
      return this.countryAlpha2ToAlpha3[value] || null; // Convert alpha-2 to alpha-3 for country codes
    }
  
    return value;
  }
  
  resetMap(): void {
    // Clear the search input
    const input = document.getElementById('autocomplete') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
    // Hide the marker if it exists
    if (this.marker) {
      this.marker.setVisible(false);
    }
    // Clear pending location
    this._pendingLocation = null;
    // Emit null to clear the selected place
    this.placeSelected.emit(null);
  }

  populateLocation(locationData: any): void {
    if (!locationData || !Array.isArray(locationData) || locationData.length === 0) {
      return;
    }

    const location = locationData[0];
    const input = document.getElementById('autocomplete') as HTMLInputElement;
    
    if (input && location.place) {
      input.value = location.place;
    }

    if (location.latitude && location.longitude && this.map && this.marker) {
      const position = {
        lat: location.latitude,
        lng: location.longitude
      };
      
      this.map.setCenter(position);
      this.marker.setPosition(position);
      this.marker.setVisible(true);
    }
  }
}
